import {
  ChatBotUIDateSlot,
  ChatBotUIInput,
  ChatBotUIMessagePayload,
  ChatBotUIProduct,
  ChatBotUIProductPriceSummary,
  ChatBotUISendMessagePayload,
} from "@jugl-web/domain-resources/chat-bot";
import {
  CTAToolResultInput,
  CTAToolResultShopifyProduct,
  CTAToolResultSlot,
  CTAToolResultSquareProduct,
  ChatBotMessageCTAToolResult,
  SupportedChatMessagePayloadActions,
} from "@jugl-web/rest-api/integrations/models/ChatBotMessage";

/**
 * Maps the raw CTA inputs tool result to the internal model
 */
const transformCTAToolResultInputsToChatBotUIInputs = (
  inputs: CTAToolResultInput[]
): ChatBotUIInput[] =>
  inputs.map((input) => ({
    name: input.title,
    type: input.type === "phone" ? "tel" : input.type,
    isRequired: input.required,
  }));

/**
 * Figures out how to display a product's price — as a single amount, a
 * range, or "variable" when no price information is available
 */
const resolvePriceSummary = (
  amounts: (number | null)[]
): ChatBotUIProductPriceSummary => {
  const nonNullableAmounts = amounts.filter(
    (amount): amount is number => amount !== null
  );

  if (nonNullableAmounts.length === 0) {
    return {
      type: "variable",
    };
  }

  const minAmount = Math.min(...nonNullableAmounts);
  const maxAmount = Math.max(...nonNullableAmounts);

  if (minAmount === maxAmount) {
    return {
      type: "fixed",
      amount: minAmount,
    };
  }

  return {
    type: "range",
    minAmount,
    maxAmount,
  };
};

/**
 * Picks the first available image to use as the product's cover photo
 */
const resolveCoverImageUrl = (imageUrls: (string | null)[]) =>
  imageUrls.find((url) => url !== null) || null;

/**
 * Maps the raw Shopify products tool result to the internal model. Products that are
 * completely out of stock are excluded from the list.
 */
const transformCTAToolResultShopifyProductsToChatBotUIProducts = (
  products: CTAToolResultShopifyProduct[]
): ChatBotUIProduct[] => {
  return products.reduce<ChatBotUIProduct[]>((acc, product) => {
    const availableVariants = product.variants.filter(
      (variant) => variant.available
    );

    // No variants available - skip the product
    if (availableVariants.length === 0) {
      return acc;
    }

    const imageUrls = availableVariants.map((v) => v.image_url);

    const validatePrice = (price: string) => {
      const priceNumber = Number(price);

      if (Number.isNaN(priceNumber) || priceNumber === 0) {
        return null;
      }

      return priceNumber;
    };

    const priceAmounts = availableVariants.map((v) => validatePrice(v.price));

    const currencyCode = availableVariants[0].currency;

    const result: ChatBotUIProduct = {
      id: product.id,
      title: product.name,
      coverImageUrl: resolveCoverImageUrl(imageUrls),
      priceSummary: resolvePriceSummary(priceAmounts),
      currencyCode,
      variants: availableVariants.map((v) => ({
        id: v.variant_id,
        title: v.title,
        price: validatePrice(v.price),
        imageUrl: v.image_url,
        selectedOptions: v.selected_options,
        availableQuantity: v.quantity_available || null,
      })),
    };

    return [...acc, result];
  }, []);
};

/**
 * Maps the raw Square products tool result to the internal model. Handles Square-specific
 * quirks so the product data looks consistent regardless of how the seller
 * configured their catalog
 */
const transformCTAToolResultSquareProductsToChatBotUIProducts = (
  products: CTAToolResultSquareProduct[]
): ChatBotUIProduct[] => {
  return products.reduce<ChatBotUIProduct[]>((acc, product) => {
    const availableVariants = product.variations.filter(
      (variation) => variation.quantity !== "0"
    );

    // No variants available - skip the product
    if (availableVariants.length === 0) {
      return acc;
    }

    const imageUrls = availableVariants.map((v) => v.image_url);

    const priceAmounts = availableVariants.map((v) =>
      v.price_money ? v.price_money.amount / 100 : null
    );

    const currencyCode =
      availableVariants.find((v) => v.price_money)?.price_money?.currency ||
      "USD";

    const shouldUseSyntheticVariantOption =
      availableVariants.length > 1 &&
      availableVariants.some((v) => v.selected_options.length === 0);

    const result: ChatBotUIProduct = {
      id: product.id,
      title: product.name,
      coverImageUrl: product.image_url || resolveCoverImageUrl(imageUrls),
      priceSummary: resolvePriceSummary(priceAmounts),
      currencyCode,
      variants: availableVariants.map((v) => ({
        id: v.id,
        title: v.name,
        price: v.price_money ? v.price_money.amount / 100 : null,
        imageUrl: v.image_url,
        selectedOptions: shouldUseSyntheticVariantOption
          ? // Square allows ad-hoc variations without option sets. If any variation is missing
            // selected options, normalize all variations to one synthetic option group to
            // avoid mixed structures in the UI
            [{ name: "Variant", value: v.name }]
          : v.selected_options,
        availableQuantity:
          v.quantity === null || Number.isNaN(Number(v.quantity))
            ? null
            : Number(v.quantity),
      })),
    };

    return [...acc, result];
  }, []);
};

/**
 * Maps the raw booking slots tool result to the internal model
 */
const transformCTAToolResultSlotsToChatBotUIDateSlots = (
  slots: CTAToolResultSlot[]
): ChatBotUIDateSlot[] =>
  slots.map((slot) => ({
    id: slot.slot_id,
    startTime: new Date(slot.start_time),
    endTime: new Date(slot.end_time),
  }));

interface BuildChatBotUIMessagePayloadOptions {
  plainText: string;
  quickReplies: string[];
  ctaToolResult?: ChatBotMessageCTAToolResult;
}

/**
 * Builds a ChatBotUIMessagePayload based on the universal parameters.
 * These parameters can come from socket messages or REST API responses.
 */
export const buildChatBotUIMessagePayload = ({
  plainText,
  quickReplies,
  ctaToolResult,
}: BuildChatBotUIMessagePayloadOptions): ChatBotUIMessagePayload => {
  const basicTextMessage: ChatBotUIMessagePayload = {
    type: "text",
    text: plainText,
    quickReplies,
  };

  // Basic text message
  if (!ctaToolResult) {
    return basicTextMessage;
  }

  try {
    // Message with form inputs
    if (
      ctaToolResult.type === "inputs" ||
      ctaToolResult.type === "collect_lead_info"
    ) {
      return {
        type: "inputs",
        text: plainText,
        inputs: transformCTAToolResultInputsToChatBotUIInputs(
          ctaToolResult.content
        ),
      };
    }

    // Message with Shopify products list
    if (ctaToolResult.type === "list_shopify_products") {
      return {
        type: "productList",
        text: plainText,
        products: transformCTAToolResultShopifyProductsToChatBotUIProducts(
          ctaToolResult.content.products
        ),
      };
    }

    // Message with Square products list
    if (ctaToolResult.type === "list_square_products") {
      return {
        type: "productList",
        text: plainText,
        products: transformCTAToolResultSquareProductsToChatBotUIProducts(
          ctaToolResult.content.products
        ),
      };
    }

    // Message with date slot booking (for services like Calendly)
    if (ctaToolResult.type === "get_slots") {
      return {
        type: "dateSlotBooking",
        text: plainText,
        timezoneCode: ctaToolResult.content.tz,
        slots: transformCTAToolResultSlotsToChatBotUIDateSlots(
          ctaToolResult.content.slots
        ),
      };
    }
  } catch {
    // Something went wrong while transforming the CTA tool result.
    // Fallback to basic text message.
    return basicTextMessage;
  }

  // Fallback to basic text message in case we received an unsupported CTA tool result type
  return basicTextMessage;
};

interface ChatBotMessageBackendResponsePayload {
  text: string;
  type: "text" | "json";
  timestamp: number;
}

/**
 * Transforms a ChatBotUISendMessagePayload to a backend response payload
 */
export const transformChatBotUIMessagePayloadToBackendResponsePayload = (
  payload: ChatBotUISendMessagePayload
): ChatBotMessageBackendResponsePayload => {
  if (payload.type === "text") {
    return {
      type: "text",
      text: payload.text,
      timestamp: Date.now(),
    };
  }

  if (payload.type === "submit-form") {
    const json = {
      action: "submit_form",
      form_data: payload.data.reduce<Record<string, string>>((acc, field) => {
        acc[field.name] = field.value;
        return acc;
      }, {}),
    } satisfies SupportedChatMessagePayloadActions["SubmitFormAction"];

    return {
      type: "json",
      text: JSON.stringify(json),
      timestamp: Date.now(),
    };
  }

  if (payload.type === "add-to-cart") {
    const json = {
      action: "add_to_cart",
      product: {
        id: payload.data.variant.id,
        name: `${payload.data.product.title} - ${payload.data.variant.title}`,
        quantity: payload.data.quantity,
      },
    } satisfies SupportedChatMessagePayloadActions["AddToCartAction"];

    return {
      type: "json",
      text: JSON.stringify(json),
      timestamp: Date.now(),
    };
  }

  throw new Error("Invalid message type");
};
